<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h4 class="mb-0">
        <a-icon type="check-circle" class="mr-2" style="color: #52c41a" />
        {{ $t("encaissementValidation.title") }}
      </h4>
    </div>
    <div class="card-body">
      <!-- Filters -->
      <div class="filters-section decaissement-filters-stacked">
        <div class="filters-header decaissement-filters-header">
          <h4>
            <a-icon type="filter" class="filter-icon" />
            {{ $t("all.filtresEtRecherche") }}
          </h4>
          <div class="action-button-group">
            <a-button class="action-button" @click="resetFilters">
              {{ $t("factures.resetFilters") }}
            </a-button>
            <a-button
              type="primary"
              class="action-button"
              @click="applyFilters"
            >
              {{ $t("action.search") }}
            </a-button>
          </div>
        </div>
        <a-row :gutter="[16, 16]" type="flex" class="decaissement-filters-row">
          <a-col :xs="24" :sm="12" :md="12" :lg="6" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="search" class="action-icon" />
                {{ $t("encaissementValidation.searchPayeur") }}
              </label>
              <a-input
                v-model="filters.search"
                allow-clear
                class="filter-control"
                :placeholder="$t('encaissementValidation.searchPayeur')"
                @pressEnter="applyFilters"
              >
                <a-icon slot="prefix" type="search" />
              </a-input>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="12" :lg="6" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="check-circle" class="action-icon" />
                {{ $t("encaissementValidation.colStatut") }}
              </label>
              <a-select
                v-model="filters.status"
                allow-clear
                class="filter-control"
                :placeholder="$t('all.tpiAll')"
                @change="applyFilters"
              >
                <a-select-option value="pending">{{
                  $t("encaissementValidation.statusPending")
                }}</a-select-option>
                <a-select-option value="received">{{
                  $t("encaissementValidation.statusReceived")
                }}</a-select-option>
                <a-select-option value="cancelled">{{
                  $t("encaissementValidation.statusCancelled")
                }}</a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="12" :lg="6" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="usergroup-add" class="action-icon" />
                {{ $t("encaissementValidation.filterBenType") }}
              </label>
              <a-select
                v-model="filters.beneficiaryType"
                allow-clear
                class="filter-control ben-type-filter-select"
                :placeholder="$t('all.tpiAll')"
                @change="applyFilters"
              >
                <a-select-option
                  value="provider"
                  :label="$t('factures.beneficiaryProvider')"
                >
                  <beneficiary-type-display type="provider" />
                </a-select-option>
                <a-select-option
                  value="associate"
                  :label="$t('factures.beneficiaryAssociate')"
                >
                  <beneficiary-type-display type="associate" />
                </a-select-option>
                <a-select-option
                  value="employee"
                  :label="$t('factures.beneficiaryEmployee')"
                >
                  <beneficiary-type-display type="employee" />
                </a-select-option>
                <a-select-option
                  value="company"
                  :label="$t('factures.beneficiaryCompany')"
                >
                  <beneficiary-type-display type="company" />
                </a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="12" :lg="6" class="filter-col">
            <div class="filter-item filter-item-stack">
              <label class="filter-label">
                <a-icon type="calendar" class="action-icon" />
                {{ $t("encaissementValidation.filterDateRange") }}
              </label>
              <a-range-picker
                v-model="filters.dateRange"
                class="filter-control date-range-filter"
                format="DD/MM/YYYY"
                style="width: 100%"
                @change="applyFilters"
              />
            </div>
          </a-col>
        </a-row>
      </div>

      <!-- Legend -->
      <div class="legend-bar mb-3">
        <div class="legend-bar-inner">
          <span class="legend-item"
            ><a-icon type="wallet" class="legend-cash" />
            {{ $t("decaissement.paymentMethod.cash") }}</span
          >
          <span class="legend-item"
            ><a-icon type="credit-card" class="legend-cheque" />
            {{ $t("decaissement.paymentMethod.cheque") }}</span
          >
          <span class="legend-item"
            ><a-icon type="bank" class="legend-virement" />
            {{ $t("decaissement.paymentMethod.virement") }}</span
          >
          <span class="legend-item"
            ><a-icon type="file-protect" class="legend-traite" />
            {{ $t("decaissement.paymentMethod.traite") }}</span
          >
          <span class="legend-item"
            ><a-icon type="tablet" class="legend-tpe" />
            {{ $t("decaissement.paymentMethod.tpe") }}</span
          >
        </div>
      </div>

      <!-- Table -->
      <div class="table-container">
        <a-table
          row-key="_id"
          :loading="loading"
          :columns="columns"
          :data-source="rows"
          :pagination="tablePagination"
          :scroll="{ x: 'max-content' }"
          :custom-row="rowClickHandler"
          :row-class-name="rowClassName"
          @change="onTableChange"
        >
          <template slot="payeur" slot-scope="text, record">
            <span>
              <a-icon
                :type="beneficiaryIcon(record._beneficiaryType)"
                class="mr-1"
              />
              {{ text || "—" }}
            </span>
          </template>
          <template slot="amount" slot-scope="text">
            <span class="amount-cell">{{
              text != null ? text.toFixed(3) : "—"
            }}</span>
          </template>
          <template slot="paymentMethod" slot-scope="text, record">
            <span
              v-if="!record._paymentMethods || !record._paymentMethods.length"
              class="text-muted"
              >—</span
            >
            <span
              v-else-if="record._paymentMethods.length > 1"
              class="payment-method-cell payment-method-multi"
            >
              <a-icon
                v-for="m in record._paymentMethods"
                :key="m"
                :type="paymentMethodIcon(m)"
                :class="paymentMethodLegendClass(m)"
                class="mixed-icon mr-1"
              />
            </span>
            <span v-else class="payment-method-cell payment-method-single">
              <a-icon
                :type="paymentMethodIcon(record._paymentMethods[0])"
                :class="paymentMethodLegendClass(record._paymentMethods[0])"
                class="mr-1"
              />
              {{
                $t("decaissement.paymentMethod." + record._paymentMethods[0])
              }}
            </span>
          </template>
          <template slot="date" slot-scope="text">
            <span class="date-cell">{{ formatDate(text) }}</span>
          </template>
          <template slot="status" slot-scope="text">
            <a-tag :color="statusColor(text)">{{
              $t("encaissementValidation.status" + capitalize(text))
            }}</a-tag>
          </template>
          <template slot="depensePaymentStatus" slot-scope="text, record">
            <a-tag :color="outboundPaymentStatusColor(record._outboundPaymentStatus)">
              {{ outboundPaymentStatusLabel(record) }}
            </a-tag>
          </template>
          <template slot="actions" slot-scope="text, record">
            <a-tooltip
              v-if="record.status === 'pending' && !record._canConfirmReceipt"
              :title="$t('encaissementValidation.confirmDisabledHint')"
            >
              <span>
                <a-button
                  type="primary"
                  size="small"
                  icon="check"
                  disabled
                  @click.stop
                >
                  {{ $t("encaissementValidation.confirm") }}
                </a-button>
              </span>
            </a-tooltip>
            <a-button
              v-else-if="record.status === 'pending'"
              type="primary"
              size="small"
              icon="check"
              @click.stop="openConfirmModal(record)"
            >
              {{ $t("encaissementValidation.confirm") }}
            </a-button>
            <a-button
              v-else
              type="link"
              size="small"
              @click.stop="openDrawer(record._id)"
            >
              <a-icon type="eye" />
            </a-button>
          </template>
        </a-table>
      </div>
      <div
        v-if="!loading && rows.length === 0"
        class="text-center text-muted py-4 empty-state"
      >
        {{ $t("encaissementValidation.noSchedules") }}
      </div>
    </div>

    <!-- Confirm Receipt Modal -->
    <a-modal
      :visible="confirmModalVisible"
      :title="$t('encaissementValidation.confirmTitle')"
      :confirm-loading="confirming"
      :width="confirmModalWidth"
      @ok="submitConfirm"
      @cancel="confirmModalVisible = false"
      destroy-on-close
    >
      <div class="mb-3">
        <div class="text-muted mb-1">
          {{ $t("encaissementValidation.scheduleTotal") }}
        </div>
        <a-input-number
          :value="selectedScheduleAmount"
          disabled
          style="width: 100%"
          :precision="3"
        />
      </div>
      <p class="text-muted small mb-2">
        {{ $t("encaissementValidation.splitPaymentsHint") }}
      </p>

      <div
        v-for="(line, idx) in paymentLines"
        :key="line.uid"
        class="payment-split-card mb-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>{{
            $t("encaissementValidation.paymentLine", { n: idx + 1 })
          }}</strong>
          <a-button
            v-if="paymentLines.length > 1"
            type="link"
            size="small"
            class="text-danger p-0"
            @click="removePaymentLine(idx)"
          >
            {{ $t("encaissementValidation.removePaymentLine") }}
          </a-button>
        </div>
        <a-row :gutter="[12, 8]">
          <a-col :xs="24" :sm="12">
            <div class="mb-2">
              <label class="filter-label">{{
                $t("encaissementValidation.paymentMethod")
              }}</label>
              <a-select
                v-model="line.method"
                style="width: 100%"
                @change="onLineMethodChange(idx)"
              >
                <a-select-option
                  v-for="m in encaissementSplitLineMethods"
                  :key="m"
                  :value="m"
                  >{{ $t(`decaissement.paymentMethod.${m}`) }}</a-select-option
                >
              </a-select>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12">
            <div class="mb-2">
              <label class="filter-label">{{ $t("recette.montant") }}</label>
              <a-input-number
                v-model="line.amount"
                :min="0"
                :precision="3"
                style="width: 100%"
              />
            </div>
          </a-col>
        </a-row>
        <template v-if="line.method === 'cash'">
          <div class="mb-2">
            <label class="filter-label">{{
              $t("decaissement.fundSource")
            }}</label>
            <a-select
              v-model="line.fundDestination"
              style="width: 100%"
              @change="onLineFundDestinationChange(idx)"
            >
              <a-select-option value="myCaisse" :disabled="!myCaisseId">{{
                $t("decaissement.myCaisse")
              }}</a-select-option>
              <a-select-option
                value="otherCaisse"
                :disabled="!otherCaissesDeduped.length"
                >{{ $t("decaissement.otherCaisse") }}</a-select-option
              >
            </a-select>
          </div>
          <div v-if="line.fundDestination === 'myCaisse'" class="mb-2">
            <label class="filter-label">{{
              $t("decaissement.cashName")
            }}</label>
            <!-- Enabled as soon as the user owns more than one caisse. -->
            <a-select
              v-model="line.caisseId"
              style="width: 100%"
              :disabled="myCaisses.length <= 1"
              :placeholder="$t('decaissement.validation.selectCaisse')"
            >
              <a-select-option v-for="c in myCaisses" :key="c._id" :value="c._id"
                >{{ c.name }}</a-select-option
              >
            </a-select>
          </div>
          <div v-if="line.fundDestination === 'otherCaisse'" class="mb-2">
            <label class="filter-label">{{
              $t("decaissement.cashName")
            }}</label>
            <a-select
              v-model="line.caisseId"
              style="width: 100%"
              :placeholder="$t('requis.required')"
            >
              <a-select-option
                v-for="c in otherCaissesDeduped"
                :key="c._id"
                :value="c._id"
                >{{ c.name }}</a-select-option
              >
            </a-select>
          </div>
        </template>
        <div v-else class="mb-2">
          <label class="filter-label">{{
            $t("decaissement.caisseBank")
          }}</label>
          <a-select
            v-model="line.caisseBankId"
            style="width: 100%"
            :placeholder="$t('requis.required')"
          >
            <a-select-option
              v-for="b in caisseBanksList"
              :key="b._id"
              :value="b._id"
              >{{ b.name }}</a-select-option
            >
          </a-select>
        </div>

        <pay-cheque-section
          v-if="line.method === 'cheque'"
          :rows="line.chequeRows"
          :total-required="Number(line.amount) || 0"
          @update="(i, f, v) => updateLineDetailRow(idx, 'chequeRows', i, f, v)"
          @add="addLineDetailRow(idx, 'chequeRows')"
          @remove="removeLineDetailRow(idx, 'chequeRows', $event)"
        />
        <pay-traite-section
          v-if="line.method === 'traite'"
          :rows="line.traiteRows"
          :show-rib="true"
          :total-required="Number(line.amount) || 0"
          @update="(i, f, v) => updateLineDetailRow(idx, 'traiteRows', i, f, v)"
          @add="addLineDetailRow(idx, 'traiteRows')"
          @remove="removeLineDetailRow(idx, 'traiteRows', $event)"
        />
        <pay-virement-section
          v-if="line.method === 'virement'"
          :rows="line.virementRows"
          :total-required="Number(line.amount) || 0"
          @update="
            (i, f, v) => updateLineDetailRow(idx, 'virementRows', i, f, v)
          "
          @add="addLineDetailRow(idx, 'virementRows')"
          @remove="removeLineDetailRow(idx, 'virementRows', $event)"
        />
      </div>

      <a-button
        type="dashed"
        block
        icon="plus"
        class="mb-3"
        @click="addPaymentLine"
      >
        {{ $t("encaissementValidation.addPaymentLine") }}
      </a-button>

      <div
        class="sum-bar mb-3"
        :class="{ match: splitsSumOk, mismatch: !splitsSumOk }"
      >
        <span
          >{{ $t("encaissementValidation.splitsTotal") }}:
          {{ splitsSum.toFixed(3) }} /
          {{ selectedScheduleAmount.toFixed(3) }}</span
        >
        <a-tag :color="splitsSumOk ? 'green' : 'red'">{{
          splitsSumOk ? "OK" : $t("encaissementValidation.splitsSumMismatch")
        }}</a-tag>
      </div>

      <div>
        <label class="filter-label">{{
          $t("decaissement.receiptNumber")
        }}</label>
        <a-input v-model="receiptNumber" />
      </div>
    </a-modal>

    <!-- Detail Drawer -->
    <encaissement-detail-drawer
      :visible="drawerVisible"
      :schedule-id="drawerScheduleId"
      @close="drawerVisible = false"
    />
  </div>
</template>

<script>
import moment from "moment";
import {
  fetchAllInverseSchedules,
  confirmInverseReceipt,
} from "@/services/api/compV3/encaissement";
import { fetchDecaissementLookups } from "@/services/api/compV3/decaissement";
import { showApiError } from "@/utilities/showApiError";
import { ENCAISSEMENT_SPLIT_LINE_METHODS } from "@/utilities/depense/encaissementFundSplitMethods";
import PayChequeSection from "../components/paymentDetails/PayChequeSection.vue";
import PayTraiteSection from "../components/paymentDetails/PayTraiteSection.vue";
import PayVirementSection from "../components/paymentDetails/PayVirementSection.vue";
import EncaissementDetailDrawer from "./EncaissementDetailDrawer.vue";
import BeneficiaryTypeDisplay from "../components/BeneficiaryTypeDisplay.vue";

export default {
  name: "EncaissementValidationPage",
  components: {
    PayChequeSection,
    PayTraiteSection,
    PayVirementSection,
    EncaissementDetailDrawer,
    BeneficiaryTypeDisplay,
  },
  data() {
    return {
      rows: [],
      loading: false,
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: {
        search: "",
        status: undefined,
        beneficiaryType: undefined,
        dateRange: [],
      },
      confirmModalVisible: false,
      confirming: false,
      selectedSchedule: null,
      lookups: { myCaisses: [], otherCaisses: [], caisseBanks: [] },
      paymentLines: [],
      receiptNumber: "",
      drawerVisible: false,
      drawerScheduleId: null,
    };
  },
  computed: {
    selectedScheduleAmount() {
      return this.selectedSchedule ? this.selectedSchedule.amount : 0;
    },
    /** A user can own several caisses, all usable whatever the active school year. */
    myCaisses() {
      return this.lookups.myCaisses || [];
    },
    myCaisseId() {
      return this.myCaisses.length ? this.myCaisses[0]._id : undefined;
    },
    otherCaissesDeduped() {
      const list = this.lookups.otherCaisses || [];
      const mine = new Set(this.myCaisses.map((c) => String(c._id)));
      if (!mine.size) return list;
      return list.filter((c) => !mine.has(String(c._id)));
    },
    splitsSum() {
      return this.paymentLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    },
    splitsSumOk() {
      return Math.abs(this.splitsSum - this.selectedScheduleAmount) < 0.01;
    },
    encaissementSplitLineMethods() {
      return ENCAISSEMENT_SPLIT_LINE_METHODS;
    },
    caisseBanksList() {
      return this.lookups.caisseBanks || [];
    },
    confirmModalWidth() {
      const wide = this.paymentLines.some((l) =>
        ["cheque", "traite", "virement", "tpe"].includes(l.method)
      );
      return wide ? 920 : 640;
    },
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        showTotal: (t) => `${t}`,
      };
    },
    columns() {
      return [
        {
          title: this.$t("encaissementValidation.colPayeur"),
          dataIndex: "_beneficiaryName",
          key: "payeur",
          ellipsis: true,
          scopedSlots: { customRender: "payeur" },
        },
        {
          title: this.$t("encaissementValidation.colLibelle"),
          dataIndex: "_expenseLabel",
          key: "libelle",
          ellipsis: true,
        },
        {
          title: this.$t("encaissementValidation.colMontant"),
          dataIndex: "amount",
          key: "amount",
          width: 120,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: this.$t("encaissementValidation.paymentMethod"),
          key: "paymentMethod",
          width: 220,
          scopedSlots: { customRender: "paymentMethod" },
        },
        {
          title: this.$t("encaissementValidation.colDueDate"),
          dataIndex: "dueDate",
          key: "dueDate",
          width: 130,
          scopedSlots: { customRender: "date" },
        },
        {
          title: this.$t("encaissementValidation.colCreatedBy"),
          dataIndex: "createdBy",
          key: "createdBy",
          width: 120,
        },
        {
          title: this.$t("encaissementValidation.colDepensePaymentStatus"),
          key: "depensePaymentStatus",
          width: 170,
          scopedSlots: { customRender: "depensePaymentStatus" },
        },
        {
          title: this.$t("encaissementValidation.colStatut"),
          dataIndex: "status",
          key: "status",
          width: 130,
          scopedSlots: { customRender: "status" },
        },
        {
          title: "",
          key: "actions",
          width: 130,
          scopedSlots: { customRender: "actions" },
        },
      ];
    },
  },
  created() {
    this.fetchData();
    this.loadLookups();
  },
  methods: {
    moment,
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YYYY") : "—";
    },
    capitalize(s) {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    paymentMethodIcon(m) {
      const map = {
        cash: "wallet",
        cheque: "credit-card",
        virement: "bank",
        traite: "file-protect",
        tpe: "tablet",
      };
      return map[m] || "dollar";
    },
    paymentMethodLegendClass(m) {
      const map = {
        cash: "legend-cash",
        cheque: "legend-cheque",
        virement: "legend-virement",
        traite: "legend-traite",
        tpe: "legend-tpe",
      };
      return map[m] || "";
    },
    statusColor(s) {
      if (s === "received") return "green";
      if (s === "pending") return "orange";
      return "default";
    },
    outboundPaymentStatusColor(key) {
      if (key === "completed") return "green";
      if (key === "awaiting_validation" || key === "pending") return "orange";
      if (key === "rejected") return "red";
      return "default";
    },
    outboundPaymentStatusLabel(record) {
      const key = record && record._outboundPaymentStatus;
      if (!key || key === "none") {
        return this.$t("encaissementValidation.outboundPaymentNone");
      }
      if (key === "rejected") {
        return this.$t("paiementHistory.validationStatuses.rejected");
      }
      const statusKey = `paiementHistory.statuses.${key}`;
      const translated = this.$t(statusKey);
      return translated !== statusKey ? translated : key;
    },
    beneficiaryIcon(type) {
      const map = {
        provider: "shop",
        associate: "team",
        employee: "user",
        company: "apartment",
      };
      return map[type] || "user";
    },
    buildQueryParams() {
      const p = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
      };
      if (this.filters.search) p.search = this.filters.search;
      if (this.filters.status) p.status = this.filters.status;
      if (this.filters.beneficiaryType)
        p.beneficiaryType = this.filters.beneficiaryType;
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        p.dueDateFrom = this.filters.dateRange[0].format("YYYY-MM-DD");
        p.dueDateTo = this.filters.dateRange[1].format("YYYY-MM-DD");
      }
      return p;
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await fetchAllInverseSchedules(this.buildQueryParams());
        const body = res.data || {};
        this.rows = body.data || [];
        this.pagination.total = body.total || 0;
        this.pagination.current = body.page || 1;
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    async loadLookups() {
      try {
        const res = await fetchDecaissementLookups({ forEncaissement: true });
        const d = res.data || {};
        const ps = d.paymentSources || {};
        this.lookups = {
          myCaisses: ps.myCaisses || (ps.myCaisse ? [ps.myCaisse] : []),
          otherCaisses: ps.otherCaisses || [],
          caisseBanks: ps.caisseBanks || [],
        };
      } catch {
        // silent
      }
    },
    applyFilters() {
      this.pagination.current = 1;
      this.fetchData();
    },
    resetFilters() {
      this.filters = {
        search: "",
        status: undefined,
        beneficiaryType: undefined,
        dateRange: [],
      };
      this.applyFilters();
    },
    onTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.fetchData();
    },
    rowClickHandler(record) {
      return { on: { click: () => this.openDrawer(record._id) } };
    },
    rowClassName(_, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },
    openDrawer(id) {
      this.drawerScheduleId = id;
      this.drawerVisible = true;
    },
    emptyChequeRow() {
      return {
        num: null,
        dateEchance: null,
        bank: undefined,
        titulaire: null,
        montant: 0,
      };
    },
    emptyTraiteRow() {
      return {
        num: null,
        dateEchance: null,
        bank: undefined,
        rib: null,
        montant: 0,
      };
    },
    emptyVirementRow() {
      return { num: null, titulaire: null, montant: 0 };
    },
    defaultCashFundFields() {
      if (this.myCaisseId) {
        return { fundDestination: "myCaisse", caisseId: this.myCaisseId };
      }
      const first = this.otherCaissesDeduped[0];
      if (first) return { fundDestination: "otherCaisse", caisseId: first._id };
      return { fundDestination: "myCaisse", caisseId: undefined };
    },
    createPaymentLine() {
      const cash = this.defaultCashFundFields();
      return {
        uid: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        method: "cash",
        amount: null,
        fundDestination: cash.fundDestination,
        caisseId: cash.caisseId,
        caisseBankId: undefined,
        chequeRows: [this.emptyChequeRow()],
        traiteRows: [this.emptyTraiteRow()],
        virementRows: [this.emptyVirementRow()],
      };
    },
    openConfirmModal(schedule) {
      if (!schedule._canConfirmReceipt) {
        this.$message.warning(this.$t("encaissementValidation.confirmDisabledHint"));
        return;
      }
      this.selectedSchedule = schedule;
      this.receiptNumber = "";
      const line = this.createPaymentLine();
      line.amount = schedule.amount;
      this.paymentLines = [line];
      this.confirmModalVisible = true;
    },
    addPaymentLine() {
      const line = this.createPaymentLine();
      line.amount = 0;
      this.paymentLines = [...this.paymentLines, line];
    },
    removePaymentLine(idx) {
      if (this.paymentLines.length <= 1) return;
      this.paymentLines = this.paymentLines.filter((_, i) => i !== idx);
    },
    onLineMethodChange(idx) {
      const line = { ...this.paymentLines[idx] };
      line.chequeRows = [this.emptyChequeRow()];
      line.traiteRows = [this.emptyTraiteRow()];
      line.virementRows = [this.emptyVirementRow()];
      if (line.method === "cash") {
        const cash = this.defaultCashFundFields();
        line.fundDestination = cash.fundDestination;
        line.caisseId = cash.caisseId;
        line.caisseBankId = undefined;
      } else {
        line.fundDestination = "caisseBank";
        line.caisseId = undefined;
        line.caisseBankId = undefined;
        if (this.caisseBanksList.length) {
          line.caisseBankId = this.caisseBanksList[0]._id;
        }
      }
      this.$set(this.paymentLines, idx, line);
    },
    onLineFundDestinationChange(idx) {
      const line = { ...this.paymentLines[idx] };
      if (line.method !== "cash") return;
      if (line.fundDestination === "myCaisse") {
        line.caisseId = this.myCaisseId;
        line.caisseBankId = undefined;
      } else if (line.fundDestination === "otherCaisse") {
        line.caisseBankId = undefined;
        const first = this.otherCaissesDeduped[0];
        line.caisseId = first ? first._id : undefined;
      }
      this.$set(this.paymentLines, idx, line);
    },
    updateLineDetailRow(lineIdx, arrKey, rowIdx, field, val) {
      const line = { ...this.paymentLines[lineIdx] };
      const arr = [...line[arrKey]];
      arr[rowIdx] = { ...arr[rowIdx], [field]: val };
      line[arrKey] = arr;
      this.$set(this.paymentLines, lineIdx, line);
    },
    addLineDetailRow(lineIdx, arrKey) {
      const templates = {
        chequeRows: this.emptyChequeRow(),
        traiteRows: this.emptyTraiteRow(),
        virementRows: this.emptyVirementRow(),
      };
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = [...line[arrKey], { ...templates[arrKey] }];
      this.$set(this.paymentLines, lineIdx, line);
    },
    removeLineDetailRow(lineIdx, arrKey, rowIdx) {
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = line[arrKey].filter((_, i) => i !== rowIdx);
      this.$set(this.paymentLines, lineIdx, line);
    },
    detailSumForLine(line) {
      if (line.method === "cheque")
        return (line.chequeRows || []).reduce(
          (s, r) => s + (Number(r.montant) || 0),
          0
        );
      if (line.method === "traite")
        return (line.traiteRows || []).reduce(
          (s, r) => s + (Number(r.montant) || 0),
          0
        );
      if (line.method === "virement")
        return (line.virementRows || []).reduce(
          (s, r) => s + (Number(r.montant) || 0),
          0
        );
      return 0;
    },
    async submitConfirm() {
      if (!this.splitsSumOk) {
        this.$message.error(
          this.$t("encaissementValidation.splitsSumMismatch")
        );
        return;
      }
      for (let i = 0; i < this.paymentLines.length; i += 1) {
        const line = this.paymentLines[i];
        const amt = Number(line.amount);
        if (!amt || amt <= 0) {
          this.$message.error(
            this.$t("encaissementValidation.lineAmountRequired")
          );
          return;
        }
        if (line.fundDestination === "caisseBank" && !line.caisseBankId) {
          this.$message.error(this.$t("requis.required"));
          return;
        }
        if (line.fundDestination === "caisseBank" && line.method === "cash") {
          this.$message.error(
            this.$t("errorCode.ENCAISSEMENT_BANK_INCOMPATIBLE_METHOD")
          );
          return;
        }
        if (line.method !== "cash" && line.fundDestination !== "caisseBank") {
          this.$message.error(
            this.$t("errorCode.ENCAISSEMENT_NON_CASH_REQUIRES_BANK")
          );
          return;
        }
        if (line.fundDestination !== "caisseBank" && !line.caisseId) {
          this.$message.error(this.$t("requis.required"));
          return;
        }
        if (["cheque", "traite", "virement"].includes(line.method)) {
          const ds = this.detailSumForLine(line);
          if (Math.abs(ds - amt) > 0.01) {
            this.$message.error(this.$t("decaissement.methodDetailsSumError"));
            return;
          }
        }
      }

      const splits = this.paymentLines.map((line) => {
        const base = {
          method: line.method,
          amount: Number(line.amount),
          fundDestination: line.fundDestination,
          caisseId:
            line.fundDestination !== "caisseBank" ? line.caisseId : undefined,
          caisseBankId:
            line.fundDestination === "caisseBank"
              ? line.caisseBankId
              : undefined,
        };
        if (line.method === "cheque")
          base.methodDetails = { cheques: line.chequeRows };
        else if (line.method === "traite")
          base.methodDetails = { traites: line.traiteRows };
        else if (line.method === "virement")
          base.methodDetails = { virements: line.virementRows };
        else base.methodDetails = {};
        return base;
      });

      this.confirming = true;
      try {
        await confirmInverseReceipt(this.selectedSchedule._id, {
          splits,
          receiptNumber: this.receiptNumber || "",
        });
        this.$message.success(this.$t("success.operationEffectue"));
        this.confirmModalVisible = false;
        this.fetchData();
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.confirming = false;
      }
    },
  },
};
</script>

<style scoped>
/* Icons + text in beneficiary dropdown */
.ben-type-filter-select >>> .ant-select-selection-selected-value {
  display: flex;
  align-items: center;
}
.ben-type-filter-select >>> .ben-type {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-bar-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px 28px;
}
.legend-bar {
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}
.legend-item {
  font-size: 12px;
  color: #595959;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.legend-cash {
  color: #52c41a;
}
.legend-cheque {
  color: #1890ff;
}
.legend-virement {
  color: #722ed1;
}
.legend-traite {
  color: #fa8c16;
}
.legend-tpe {
  color: #eb2f96;
}
.legend-mixed {
  color: #8c8c8c;
}

.payment-method-cell {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  font-size: 13px;
}
.payment-method-mixed .mixed-label {
  font-weight: 500;
  color: #595959;
}
.payment-method-mixed .mixed-icons {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.payment-method-mixed .mixed-icon {
  font-size: 16px;
}
.payment-method-single {
  color: #262626;
}

.payment-split-card {
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
}
.sum-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  font-weight: 600;
}
.sum-bar.match {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
.sum-bar.mismatch {
  background: #fff2f0;
  border: 1px solid #ffccc7;
}
</style>

<style>
@import "~@/views/compV3/common.css";
</style>
